import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { db } from "@/lib/firebase";
import {
  collection,
  query,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
  addDoc,
} from "firebase/firestore";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { LanguageSelector } from "@/components/ui/language-selector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Heart,
  Search,
  Mail,
  Phone,
  MapPin,
  Star,
  DollarSign,
  Briefcase,
  ArrowLeft,
  Loader2,
  CheckCircle,
  Clock,
  XCircle
} from "lucide-react";
import { DashboardHeader } from "@/components/layout/DashboardHeader";

// Consultant interface
interface Consultant {
  id: string;
  name: string;
  specialty: string;
  expertise?: string[];
  location?: string;
  email?: string;
  phone?: string;
  hourly_rate?: number;
  currency?: string;
  rating?: number;
  projects_completed?: number;
  availability?: string;
  bio?: string;
}

// Translations
const translations = {
  en: {
    title: "Consultant Directory",
    subtitle: "Find and connect with professional advisors",
    searchPlaceholder: "Search by name, specialty...",
    filterBySpecialty: "Filter by specialty",
    filterByAvailability: "Filter by availability",
    all: "All",
    available: "Available",
    busy: "Busy",
    unavailable: "Unavailable",
    rate: "Rate",
    perHour: "/hour",
    projects: "Projects",
    expertise: "Expertise",
    contact: "Contact",
    addToFavorites: "Add to Favorites",
    removeFromFavorites: "Remove from Favorites",
    sendContactRequest: "Send Contact Request",
    noConsultantsFound: "No consultants found",
    loading: "Loading consultants...",
    backToDashboard: "Back to Dashboard",
    addedToFavorites: "Added to favorites",
    removedFromFavorites: "Removed from favorites",
    contactRequestSent: "Contact request sent",
    finance: "Finance",
    legal: "Legal",
    tax: "Tax",
    operations: "Operations",
    marketing: "Marketing",
    hr: "Human Resources",
    it: "IT & Technology",
    strategy: "Strategy",
  },
  fr: {
    title: "Répertoire des Consultants",
    subtitle: "Trouvez et connectez-vous avec des conseillers professionnels",
    searchPlaceholder: "Rechercher par nom, spécialité...",
    filterBySpecialty: "Filtrer par spécialité",
    filterByAvailability: "Filtrer par disponibilité",
    all: "Tous",
    available: "Disponible",
    busy: "Occupé",
    unavailable: "Indisponible",
    rate: "Tarif",
    perHour: "/heure",
    projects: "Projets",
    expertise: "Expertise",
    contact: "Contact",
    addToFavorites: "Ajouter aux favoris",
    removeFromFavorites: "Retirer des favoris",
    sendContactRequest: "Envoyer une demande",
    noConsultantsFound: "Aucun consultant trouvé",
    loading: "Chargement des consultants...",
    backToDashboard: "Retour au tableau de bord",
    addedToFavorites: "Ajouté aux favoris",
    removedFromFavorites: "Retiré des favoris",
    contactRequestSent: "Demande de contact envoyée",
    finance: "Finance",
    legal: "Juridique",
    tax: "Fiscalité",
    operations: "Opérations",
    marketing: "Marketing",
    hr: "Ressources Humaines",
    it: "TI & Technologie",
    strategy: "Stratégie",
  },
};

// Fallback mock consultants
const fallbackConsultants: Consultant[] = [
  {
    id: "1",
    name: "Marie Dupont",
    specialty: "Finance",
    expertise: ["M&A Advisory", "Due Diligence", "Valuation"],
    location: "Montreal, QC",
    email: "marie.dupont@example.com",
    phone: "+1 514-555-0101",
    hourly_rate: 250,
    currency: "CAD",
    rating: 4.9,
    projects_completed: 47,
    availability: "available",
    bio: "Expert financier avec 15 ans d'expérience en fusions-acquisitions."
  },
  {
    id: "2",
    name: "Jean-Pierre Martin",
    specialty: "Legal",
    expertise: ["Corporate Law", "Contract Negotiation", "Regulatory Compliance"],
    location: "Toronto, ON",
    email: "jp.martin@example.com",
    phone: "+1 416-555-0202",
    hourly_rate: 350,
    currency: "CAD",
    rating: 4.8,
    projects_completed: 62,
    availability: "available",
    bio: "Avocat spécialisé en droit des affaires et transactions commerciales."
  },
  {
    id: "3",
    name: "Sophie Bernard",
    specialty: "Tax",
    expertise: ["Tax Planning", "Cross-Border Transactions", "Estate Planning"],
    location: "Vancouver, BC",
    email: "sophie.b@example.com",
    phone: "+1 604-555-0303",
    hourly_rate: 275,
    currency: "CAD",
    rating: 4.7,
    projects_completed: 35,
    availability: "busy",
    bio: "Conseillère fiscale certifiée avec expertise internationale."
  },
  {
    id: "4",
    name: "Robert Chen",
    specialty: "Operations",
    expertise: ["Process Optimization", "Supply Chain", "Turnaround Management"],
    location: "Calgary, AB",
    email: "r.chen@example.com",
    phone: "+1 403-555-0404",
    hourly_rate: 225,
    currency: "CAD",
    rating: 4.6,
    projects_completed: 28,
    availability: "available",
    bio: "Consultant en opérations avec focus sur l'amélioration continue."
  },
];

export default function SellerConsultants() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const t = translations[language as keyof typeof translations] || translations.en;

  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [specialtyFilter, setSpecialtyFilter] = useState("all");
  const [availabilityFilter, setAvailabilityFilter] = useState("all");
  const [favoriteConsultants, setFavoriteConsultants] = useState<string[]>([]);

  // Load consultants from Firestore
  useEffect(() => {
    const loadConsultants = async () => {
      setIsLoading(true);
      try {
        const consultantsRef = collection(db, "consultants");
        const snapshot = await getDocs(consultantsRef);
        
        if (snapshot.empty) {
          setConsultants(fallbackConsultants);
        } else {
          const data = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as Consultant[];
          setConsultants(data);
        }
      } catch (error) {
        console.error("Error loading consultants:", error);
        setConsultants(fallbackConsultants);
      } finally {
        setIsLoading(false);
      }
    };

    loadConsultants();
  }, []);

  // Load and listen to favorites
  useEffect(() => {
    if (!user?.id) return;
    
    const favCol = collection(db, "users", user.id, "consultantsFavorites");
    const unsub = onSnapshot(favCol, (snap) => {
      const ids: string[] = [];
      snap.forEach((d) => ids.push(d.id));
      setFavoriteConsultants(ids);
    });
    
    return () => unsub();
  }, [user?.id]);

  // Toggle favorite
  const toggleFavorite = async (consultantId: string, consultantName: string) => {
    if (!user?.id) return;
    
    const favRef = doc(db, "users", user.id, "consultantsFavorites", consultantId);
    const isFavorite = favoriteConsultants.includes(consultantId);
    
    try {
      if (isFavorite) {
        await deleteDoc(favRef);
        toast({
          title: t.removedFromFavorites,
          description: consultantName,
        });
      } else {
        await setDoc(favRef, { addedAt: serverTimestamp() });
        toast({
          title: t.addedToFavorites + " ❤️",
          description: consultantName,
        });
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  // Send contact request
  const sendContactRequest = async (consultant: Consultant) => {
    if (!user?.id) return;
    
    try {
      await addDoc(collection(db, "contactRequests"), {
        fromUserId: user.id,
        fromUserEmail: user.email,
        toConsultantId: consultant.id,
        toConsultantName: consultant.name,
        toConsultantEmail: consultant.email,
        status: "pending",
        createdAt: serverTimestamp()
      });
      
      toast({
        title: t.contactRequestSent,
        description: consultant.name,
      });
    } catch (error) {
      console.error("Error sending contact request:", error);
    }
  };

  // Filter consultants
  const filteredConsultants = consultants.filter((c) => {
    const matchesSearch = !searchTerm.trim() ||
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.specialty?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.location?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSpecialty = specialtyFilter === "all" || 
      c.specialty?.toLowerCase() === specialtyFilter.toLowerCase();
    
    const matchesAvailability = availabilityFilter === "all" || 
      c.availability === availabilityFilter;
    
    return matchesSearch && matchesSpecialty && matchesAvailability;
  });

  // Get unique specialties
  const specialties = [...new Set(consultants.map(c => c.specialty).filter(Boolean))];

  // Format currency
  const formatCurrency = (amount: number, currency: string = "CAD") => {
    return new Intl.NumberFormat(language === 'fr' ? 'fr-CA' : 'en-CA', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Get availability badge
  const getAvailabilityBadge = (availability?: string) => {
    switch (availability) {
      case "available":
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />{t.available}</Badge>;
      case "busy":
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />{t.busy}</Badge>;
      case "unavailable":
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />{t.unavailable}</Badge>;
      default:
        return <Badge variant="outline">{availability || t.available}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <DashboardHeader />
        <main className="flex-1 p-6 bg-muted/30 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">{t.loading}</span>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <DashboardHeader />
      <main className="flex-1 p-6 bg-muted/30 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate("/seller/dashboard")}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t.backToDashboard}
              </Button>
            </div>
            <LanguageSelector />
          </div>

          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t.title}</h1>
            <p className="text-muted-foreground mt-2">{t.subtitle}</p>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t.searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={specialtyFilter} onValueChange={setSpecialtyFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder={t.filterBySpecialty} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.all}</SelectItem>
                {specialties.map((specialty) => (
                  <SelectItem key={specialty} value={specialty?.toLowerCase() || ""}>
                    {specialty}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={availabilityFilter} onValueChange={setAvailabilityFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder={t.filterByAvailability} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.all}</SelectItem>
                <SelectItem value="available">{t.available}</SelectItem>
                <SelectItem value="busy">{t.busy}</SelectItem>
                <SelectItem value="unavailable">{t.unavailable}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Consultants Grid */}
          {filteredConsultants.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Briefcase className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">{t.noConsultantsFound}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredConsultants.map((consultant) => {
                const isFavorite = favoriteConsultants.includes(consultant.id);
                
                return (
                  <Card key={consultant.id} className="relative hover:shadow-lg transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg">{consultant.name}</CardTitle>
                          <CardDescription className="flex items-center gap-2 mt-1">
                            <Badge variant="secondary">{consultant.specialty}</Badge>
                            {getAvailabilityBadge(consultant.availability)}
                          </CardDescription>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => toggleFavorite(consultant.id, consultant.name)}
                          className={isFavorite ? "text-red-500" : "text-muted-foreground"}
                        >
                          <Heart className={`h-5 w-5 ${isFavorite ? "fill-current" : ""}`} />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {consultant.bio && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {consultant.bio}
                        </p>
                      )}

                      {consultant.expertise && consultant.expertise.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {consultant.expertise.slice(0, 3).map((exp) => (
                            <Badge key={exp} variant="outline" className="text-xs">
                              {exp}
                            </Badge>
                          ))}
                        </div>
                      )}

                      <div className="space-y-2 text-sm">
                        {consultant.location && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <MapPin className="h-4 w-4" />
                            {consultant.location}
                          </div>
                        )}
                        {consultant.hourly_rate && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <DollarSign className="h-4 w-4" />
                            {formatCurrency(consultant.hourly_rate, consultant.currency)}{t.perHour}
                          </div>
                        )}
                        {consultant.rating && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Star className="h-4 w-4 text-yellow-500" />
                            {consultant.rating.toFixed(1)} ({consultant.projects_completed || 0} {t.projects})
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2 pt-2">
                        {consultant.email && (
                          <Button variant="outline" size="sm" asChild className="flex-1">
                            <a href={`mailto:${consultant.email}`}>
                              <Mail className="h-4 w-4 mr-1" />
                              Email
                            </a>
                          </Button>
                        )}
                        {consultant.phone && (
                          <Button variant="outline" size="sm" asChild className="flex-1">
                            <a href={`tel:${consultant.phone}`}>
                              <Phone className="h-4 w-4 mr-1" />
                              {language === 'fr' ? 'Appeler' : 'Call'}
                            </a>
                          </Button>
                        )}
                      </div>

                      <Button 
                        className="w-full" 
                        size="sm"
                        onClick={() => sendContactRequest(consultant)}
                      >
                        {t.sendContactRequest}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
